import { Button, CircularProgress, Skeleton, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  ConceptGroupAssetHeader,
  useConceptGroupMetaItems,
} from '@verantos-dev/feature-concept-group-detail';

import { useConceptGroupBreadcrumbs } from '../../../hooks/useConceptGroupBreadcrumbs';
import { useConceptGroupRename } from '../../../hooks/useConceptGroupRename';
import { ConceptGroupNameInlineEdit } from '../../ConceptGroupNameInlineEdit';
import type { ConceptGroupPreviewPageModel } from '../hooks/useConceptGroupPreviewPage';
import { useDraftConceptGroupOwnership } from '../hooks/useDraftConceptGroupOwnership';

/**
 * Draft review header. Only the draft (proposal) state renders this — once
 * created, the page redirects to the real concept-group route.
 *
 * Reads as an asset-detail page (OR-6288 pass 2) — name + Draft status chip
 * with a Save primary top-right and the condensed meta row.
 */
export const ConceptGroupDraftHeader = ({
  model,
  displayName,
  projectId,
  canEdit,
  saveDisabled,
}: {
  model: ConceptGroupPreviewPageModel;
  /** The name the user will publish (model name with stored-name fallback). */
  displayName: string;
  projectId: string;
  /** Owner-only: non-owners get a read-only header (no Save). */
  canEdit: boolean;
  /** A replacement upload is in flight — saving now would race it. */
  saveDisabled: boolean;
}) => {
  const { formatMessage } = useIntl();
  const { detail } = model;
  // The draft is already a listed asset, so publishing it to the org library
  // reads as an approval rather than a save.
  // TODO: cleanup with concept_group_approve_action ff
  const approveActionEnabled = useFeatureFlagSplit(
    'concept_group_approve_action',
    false
  ) as boolean;
  const primaryActionLabelId = approveActionEnabled
    ? model.creating
      ? 'epiChat.conceptGroupPreview.approve.approving'
      : 'epiChat.conceptGroupPreview.approve.button'
    : model.creating
    ? 'epiChat.conceptGroupPreview.create.creating'
    : 'epiChat.conceptGroupPreview.save.button';

  const errorText = model.nameErrorId ? formatMessage({ id: model.nameErrorId }) : undefined;
  // Owner + last-modified come from the project-artifact manifest (the preview
  // detail carries neither), with a current-user fallback for a brand-new draft.
  const { ownerName, lastModifiedIso, isResolving } = useDraftConceptGroupOwnership(projectId, {
    createdGroupId: model.createdGroupId,
    previewId: detail?.previewId,
    conceptGroupName: detail?.conceptGroupName,
  });
  const metaItems = useConceptGroupMetaItems({
    domains: detail?.domains ?? [],
    ownerName,
    lastModifiedIso,
  });
  // Suppress the meta row until BOTH the preview detail (domains) and the manifest
  // ownership (owner name) have resolved. Otherwise opening someone else's draft on
  // a cold refresh flashes the current-user fallback owner before the manifest lands.
  const isDetailLoading = (model.isLoading && !detail) || isResolving;

  const breadcrumbs = useConceptGroupBreadcrumbs({
    conceptGroupName: detail?.conceptGroupName,
    isLoading: model.isLoading,
  }).breadcrumbs;

  // OR-6657: renaming persists to the draft store on commit, independently of the
  // publish action below.
  const renameModel = useConceptGroupRename({
    projectId,
    previewId: detail?.previewId,
    conceptGroupId: model.createdGroupId,
    currentName: displayName,
    canEdit,
  });

  // Figma (OR-6288 pass 2): the draft reads as an asset detail page — name +
  // Draft status chip in the header with the publish primary top-right (Save,
  // or Approve under concept_group_approve_action); the "Create concept group"
  // stepper heading is gone.
  return (
    <>
      <ConceptGroupAssetHeader
        breadcrumbs={breadcrumbs}
        title={displayName}
        isLoading={isDetailLoading}
        status={formatMessage({ id: 'epiChat.conceptGroupPreview.status.draft' })}
        statusTestId="cg-preview-status-draft"
        titleContent={
          renameModel.canRename ? (
            <ConceptGroupNameInlineEdit
              testIdPrefix="cg-draft"
              hoverToEdit
              name={displayName}
              isEditing={renameModel.isEditing}
              editedName={renameModel.editedName}
              errorMessageId={renameModel.errorId}
              isEditable
              isSaving={renameModel.isSaving}
              maxLength={renameModel.maxLength}
              canCommit={renameModel.canCommit}
              onStartEdit={renameModel.startEdit}
              onChange={renameModel.changeName}
              onSave={renameModel.commit}
              onCancel={renameModel.cancelEdit}
            />
          ) : undefined
        }
        metaItems={isDetailLoading ? [] : metaItems}
        rightContent={
          // The publish action is owner-only (OR-6288 permissions); non-owners
          // get a read-only header.
          canEdit ? (
            // While the name verdict is outstanding the action is unknown, not
            // under way — a spinner inside the button would read as approving.
            model.isCheckingName ? (
              <Skeleton
                variant="rounded"
                width={96}
                height={31}
                data-testid="cg-preview-create-button-loading"
              />
            ) : (
              <Button
                variant="contained"
                size="small"
                onClick={model.handleCreate}
                disabled={
                  saveDisabled ||
                  model.creating ||
                  !model.name.trim() ||
                  model.isLoading ||
                  model.isNameTaken ||
                  // A rename owns the name until it lands: the editor holds a value the
                  // page has not taken, and stays open across the save that commits it.
                  renameModel.isEditing
                }
                startIcon={
                  model.creating ? <CircularProgress size={16} color="inherit" /> : undefined
                }
                sx={{ textTransform: 'none' }}
                data-testid="cg-preview-create-button"
              >
                {formatMessage({ id: primaryActionLabelId })}
              </Button>
            )
          ) : undefined
        }
      />
      {/* One home at a time: while the title is being edited the field owns name
          errors, so this row stays silent rather than repeating them. */}
      {errorText && !renameModel.isEditing ? (
        <Typography
          variant="caption"
          color="error"
          sx={{ paddingX: 6, paddingBottom: 1 }}
          data-testid="cg-preview-name-error"
        >
          {errorText}
        </Typography>
      ) : null}
    </>
  );
};
